import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { IToastData, Toast } from '../index.tsx';

interface IToastContext {
  open: (toast: Omit<IToastData, 'id'>) => void;
  toasts: IToastData[];
}

const ToastContext = createContext<IToastContext | undefined>(undefined);

export const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toasts, setToasts] = useState<IToastData[]>([]);

  const open = useCallback(({ title, description, status, timeout }: Omit<IToastData, 'id'>) => {
    setToasts((prevState) => {
      return [
        ...prevState,
        { title: title, description: description, status: status, id: Date.now(), timeout: timeout },
      ];
    });
  }, []);

  const handleClose = useCallback((toastId: number) => {
    setToasts((prevState) => prevState.filter((toastFilter) => toastFilter.id !== toastId));
  }, []);

  const value = useMemo(
    () => ({
      toasts,
      open,
    }),
    [toasts, open]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className='absolute top-[65px] right-1 max-lg:left-1/2 max-lg:right-auto max-lg:-translate-x-1/2'>
        <div>
          {toasts.map((toast) => (
            <Toast
              id={toast.id}
              key={toast.id}
              status={toast.status}
              title={toast.title}
              description={toast.description}
              timeout={toast.timeout}
              onClose={handleClose}
            />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }

  return context;
};
